/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

// Short-viewport threshold (landscape phones and similar). Use when extra
// height-saving (smaller fonts, tighter spacing) is needed.
export const SHORT_VIEWPORT_QUERY = "(max-height: 599.95px)";
export const SHORT_VIEWPORT = `@media ${SHORT_VIEWPORT_QUERY}`;

// Narrow-viewport threshold (mobile, matches MUI's `sm` breakpoint).
export const NARROW_VIEWPORT_QUERY = "(max-width: 599.95px)";
export const NARROW_VIEWPORT = `@media ${NARROW_VIEWPORT_QUERY}`;

// Wide-viewport threshold (tablet/desktop, matches MUI's `sm` breakpoint and up).
export const WIDE_VIEWPORT_QUERY = "(min-width: 600px)";
export const WIDE_VIEWPORT = `@media ${WIDE_VIEWPORT_QUERY}`;

// Matches narrow widths or short heights. Use as a single sx key when the same
// compact styles should apply to both.
export const COMPACT_VIEWPORT = `@media ${NARROW_VIEWPORT_QUERY}, ${SHORT_VIEWPORT_QUERY}`;

// Returns a style fragment that uses 100vh as the base value and overrides with
// 100dvh inside an @supports block, so older browsers without dvh support get
// the vh fallback. Spread into a makeStyles object or sx prop:
//
//   { ...dvhFallback('maxHeight', 300) }
//   sx={{ ...dvhFallback('height') }}
export const dvhFallback = (
  property: "height" | "minHeight" | "maxHeight",
  offsetPx = 0,
  important = false
) => {
  const suffix = important ? " !important" : "";
  const vh = offsetPx
    ? `calc(100vh - ${offsetPx}px)${suffix}`
    : `100vh${suffix}`;
  const dvh = offsetPx
    ? `calc(100dvh - ${offsetPx}px)${suffix}`
    : `100dvh${suffix}`;
  return {
    [property]: vh,
    "@supports (height: 100dvh)": {
      [property]: dvh,
    },
  };
};

// sx fragment for a Box wrapping a SearchInput that should compress on short viewports.
export const SEARCH_INPUT_BOX_COMPACT_SX = {
  [COMPACT_VIEWPORT]: {
    paddingX: 1,
  },
  [SHORT_VIEWPORT]: {
    paddingX: 0,
    paddingY: 0,
    "& .MuiOutlinedInput-input": {
      paddingY: 1,
      fontSize: "1rem",
    },
    "& .MuiSvgIcon-fontSizeLarge": {
      fontSize: "1.25rem",
    },
  },
};

export const SELECT_THEME = {
  borderRadius: 0,
  colors: {
    primary: "var(--marapp-primary-color)",
    neutral0: "var(--marapp-gray-8)",
    neutral10: "var(--marapp-gray-2)",
    neutral20: "var(--marapp-gray-2)",
    neutral30: "var(--marapp-gray-2)",
    neutral50: "var(--marapp-gray-1)",
    neutral60: "var(--marapp-gray-1)",
    neutral80: "var(--marapp-gray-8)",
  },
};

export const CUSTOM_STYLES = {
  valueContainer: (provided, state) => ({
    ...provided,
    boxShadow: "none",
  }),
  placeholder: () => ({
    color: "var(--marapp-gray-5)",
    maxWidth: "calc(100% - 20px)",
    overflow: "hidden",
    position: "absolute",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  input: () => ({
    boxShadow: "none",
  }),
  container: () => ({
    boxShadow: "none",
  }),
  menu: () => ({
    border: "1px solid var(--marapp-gray-1)",
    boxShadow: "none",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused
      ? "var(--marapp-gray-6)"
      : "var(--marapp-gray-8)",
    color: "var(--marapp-gray-2)",
    padding: 10,
    boxShadow: "none",
  }),
  control: (provided, state) => ({
    ...provided,
    padding: 10,
    boxShadow: "none",
  }),
  multiValue: (provided, state) => ({
    ...provided,
    borderRadius: "50px",
    display: "flex",
    ".dropdown-item-subtitle": {
      display: "none",
    },
  }),
  multiValueRemove: () => ({
    color: "var(--marapp-gray-9)",
    padding: "0px 10px",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    boxShadow: "none",
    color: "var(---marapp-gray-2)",
  }),
  clearIndicator: () => ({
    color: "var(--marapp-gray-2)",
  }),
  dropdownIndicator: () => ({
    color: "var(--marapp-gray-2)",
  }),
};
